import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Umbrella, X, Save, AlertTriangle, Info, Hash } from 'lucide-react';
import * as payrollApi from '../../../../services/api/payrollApi';
import './employee-form.css';

// InputField matches the pattern used by UpdateTaxProfileModal /
// UpdateCompensationModal — defined outside to avoid recreating on each render.
const InputField = ({ icon: Icon, label, name, type = 'number', required = false, description, value, onChange, showValidation, errors, ...props }) => (
  <div className="input-group">
    <label className="input-label" htmlFor={`input-${name}`}>
      {label}
      {required && <span className="required-indicator">*</span>}
    </label>
    {description && <p className="input-description">{description}</p>}
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      <input
        id={`input-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
        // Number inputs bump their value on scroll-wheel when focused; blur
        // on wheel so a normal page scroll doesn't silently edit the field.
        onWheel={type === 'number' ? (e) => e.currentTarget.blur() : undefined}
        {...props}
      />
    </div>
    {showValidation && errors?.[name] && (
      <div className="error-message">
        <AlertTriangle size={12} />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const UpdateVacationBalanceModal = ({ show, handleClose, employeeId, companyId, snapshot, onSuccess }) => {
  const [formData, setFormData] = useState({
    earnedDays: '',
    carriedOverDays: '',
    savedDays: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && snapshot) {
      setFormData({
        earnedDays: String(snapshot.earnedDays ?? 0),
        carriedOverDays: String(snapshot.carriedOverDays ?? 0),
        savedDays: String(snapshot.savedDays ?? 0),
      });
      setShowValidation(false);
      setErrors({});
      setError(null);
    }
  }, [show, snapshot]);

  const validateForm = () => {
    const newErrors = {};
    for (const key of ['earnedDays', 'carriedOverDays', 'savedDays']) {
      const n = Number(formData[key]);
      if (Number.isNaN(n) || n < 0) {
        newErrors[key] = 'Måste vara 0 eller större';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setShowValidation(true);
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    try {
      await payrollApi.setVacationBalance(companyId, {
        employeeId,
        year: snapshot.year,
        earnedDays: Number(formData.earnedDays),
        carriedOverDays: Number(formData.carriedOverDays),
        savedDays: Number(formData.savedDays),
      });
      if (onSuccess) onSuccess();
      resetAndClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Det gick inte att uppdatera semestersaldot');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    if (snapshot) {
      setFormData({
        earnedDays: String(snapshot.earnedDays ?? 0),
        carriedOverDays: String(snapshot.carriedOverDays ?? 0),
        savedDays: String(snapshot.savedDays ?? 0),
      });
    }
    setError(null);
    setShowValidation(false);
    setErrors({});
    handleClose();
  };

  return (
    <>
      {/* Shares the same style block as UpdateTaxProfileModal — kept local to
          avoid a cross-modal CSS dependency. */}

      <Modal className="emp-modal" show={show} onHide={resetAndClose} size="lg" backdrop="static" centered>
        <div className="vac-modal-content">
          {/* Header */}
          <div className="vac-modal-header">
            <button className="close-button" onClick={resetAndClose} aria-label="Stäng modal">
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <Umbrella size={24} color="#0891b2" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  Redigera semestersaldo
                </h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  {snapshot
                    ? `Semesterår ${snapshot.year}/${snapshot.year + 1} (${snapshot.dateRange?.from} – ${snapshot.dateRange?.to})`
                    : 'Aktuellt semesterår'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="vac-modal-body">
            <Form onSubmit={handleSubmit}>
              {error && (
                <div className="validation-summary">
                  <AlertTriangle size={16} className="validation-icon" />
                  <div className="validation-content">
                    <div className="validation-title">Ett fel uppstod</div>
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>{error}</p>
                  </div>
                </div>
              )}

              {/* Read-only context: what's currently on file */}
              {snapshot && (
                <div className="current-value-card">
                  <Umbrella size={20} className="current-value-icon" />
                  <div className="current-value-content">
                    <div className="current-value-label">Nuvarande saldo</div>
                    <div className="current-value-text">
                      Tjänat {snapshot.earnedDays} · Överförda {snapshot.carriedOverDays} · Sparat {snapshot.savedDays}
                      {' — '}Använd {snapshot.usedDays} · Kvar {snapshot.remainingPaidDays}
                      {(snapshot.advanceDaysTaken || 0) > 0 && ` · Förskott ${snapshot.advanceDaysTaken}`}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-info">
                <Info size={16} className="info-icon" />
                <div className="info-text">
                  <strong>Information:</strong> Endast <em>Tjänat</em>, <em>Överförda</em> och <em>Sparat</em> redigeras här.
                  <em> Använd</em> beräknas automatiskt från godkända SEMESTER-poster på lönespecar.
                </div>
              </div>

              <div className="form-section">
                <div className="form-grid">
                  <InputField
                    icon={Hash}
                    label="Tjänat"
                    name="earnedDays"
                    type="number"
                    required={true}
                    placeholder="25"
                    description="Intjänade dagar från föregående intjänandeår (§4+§7)"
                    value={formData.earnedDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                  <InputField
                    icon={Hash}
                    label="Överförda"
                    name="carriedOverDays"
                    type="number"
                    required={true}
                    placeholder="0"
                    description="Oanvända dagar överförda från tidigare år"
                    value={formData.carriedOverDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                  <InputField
                    icon={Hash}
                    label="Sparat"
                    name="savedDays"
                    type="number"
                    required={true}
                    placeholder="0"
                    description="Sparade dagar enligt §18 (max 5/år i ackumulering)"
                    value={formData.savedDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>
            </Form>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button className="action-button btn-cancel" onClick={resetAndClose} disabled={loading}>
                Avbryt
              </button>
              <button className="action-button btn-save" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Spara semestersaldo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateVacationBalanceModal;
